import { FC, useState, useEffect } from 'react';
import { Phone, MessageCircle, ChevronRight, Loader2, AlertCircle, User } from 'lucide-react';
import { Customer } from '../../lib/types';
import { getCustomerPhoneNumbers } from '../../lib/directus';
import {
  processPhoneNumbers,
  generateWhatsAppURL,
  formatIndonesianNumber,
  PhoneNumber
} from '../../lib/phoneUtils';
import { logWhatsAppInteraction, logPhoneCallInteraction, extractCustomerDataForLogging } from '../../lib/interactionLogger';
import { Button } from './button';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Avatar, AvatarFallback } from './avatar';
import { Separator } from './separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';

interface CustomerSelectionModalProps {
  customer: Customer;
  isOpen: boolean;
  onClose: () => void;
  actionType: 'call' | 'whatsapp' | null;
  onAction?: (action: 'call' | 'whatsapp', number: string, customer: Customer) => void;
}

export const CustomerSelectionModal: FC<CustomerSelectionModalProps> = ({
  customer,
  isOpen,
  onClose,
  actionType,
  onAction
}) => {
  const { t } = useLanguage();
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && customer.idcust) {
      fetchPhoneNumbers();
    }
  }, [isOpen, customer.idcust]);

  const fetchPhoneNumbers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch phone numbers from database
      const fetchedNumbers = await getCustomerPhoneNumbers(customer.idcust!);
      const phoneData = fetchedNumbers.map(num => ({
        number: num.number,
        source: num.source
      }));
      
      // Add immediate phone data if available
      const immediatePhoneData = [
        ...(customer.phone ? [{ number: customer.phone, source: 'customer_tambak' as const }] : []),
        ...(customer.Contacs ? [{ number: customer.Contacs, source: 'customer_tambak' as const }] : [])
      ];
      
      // Merge and process all phone numbers
      const allPhoneData = [...phoneData, ...immediatePhoneData];
      const processed = processPhoneNumbers(allPhoneData);
      
      setPhoneNumbers(processed);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
      setError(t('customer_selection.error_loading_numbers'));
      
      // Fallback to immediate phone data only
      const fallbackData = [
        ...(customer.phone ? [{ number: customer.phone, source: 'customer_tambak' as const }] : []),
        ...(customer.Contacs ? [{ number: customer.Contacs, source: 'customer_tambak' as const }] : [])
      ];
      
      if (fallbackData.length > 0) {
        const processed = processPhoneNumbers(fallbackData);
        setPhoneNumbers(processed);
        setError(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNumberSelect = async (phoneNumber: PhoneNumber, action: 'call' | 'whatsapp') => {
    const cleanNumber = phoneNumber.number.replace(/[^\d+]/g, '');
    
    // Extract customer data for logging
    const customerData = extractCustomerDataForLogging(customer);
    
    if (action === 'whatsapp') {
      const customerName = customer.name || customer.NamaPelangganTambak || '';
      const whatsappUrl = generateWhatsAppURL(phoneNumber.number, customerName);
      window.open(whatsappUrl, '_blank');
      
      // Log WhatsApp interaction
      await logWhatsAppInteraction(
        customerData.customerId,
        cleanNumber,
        customerData.customerName,
        customerData.customerTambakId
      );
    } else {
      window.location.href = `tel:${cleanNumber}`;
      
      // Log phone call interaction
      await logPhoneCallInteraction(
        customerData.customerId,
        cleanNumber,
        customerData.customerName,
        customerData.customerTambakId
      );
    }
    
    onAction?.(action, cleanNumber, customer);
    onClose();
  };

  const getActionTitle = () => {
    switch (actionType) {
      case 'call':
        return t('customer_selection.call_customer');
      case 'whatsapp':
        return t('customer_selection.whatsapp_message');
      default:
        return t('customer_selection.contact_customer');
    }
  };

  const getActionIcon = () => {
    switch (actionType) {
      case 'call':
        return <Phone className="w-6 h-6 text-primary" />;
      case 'whatsapp':
        return <MessageCircle className="w-6 h-6 text-green-600" />;
      default:
        return <User className="w-6 h-6 text-muted-foreground" />;
    }
  };

  const getActionColor = () => {
    switch (actionType) {
      case 'call':
        return 'blue';
      case 'whatsapp':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getFilteredNumbers = () => {
    if (actionType === 'whatsapp') {
      return phoneNumbers.filter(num => num.type === 'whatsapp');
    }
    return phoneNumbers; // Show all numbers for call action
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'phone_customer':
        return t('customer_selection.phone_db');
      case 'customer_tambak':
        return t('customer_selection.customer_db');
      default:
        return t('customer_selection.manual_entry');
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const filteredNumbers = getFilteredNumbers();
  const actionColor = getActionColor();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Avatar className={`w-12 h-12 ${
              actionColor === 'blue' ? 'bg-primary/10' :
              actionColor === 'green' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'
            }`}>
              <AvatarFallback className={actionColor === 'blue' ? 'bg-primary/10' :
                actionColor === 'green' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}>
                {getActionIcon()}
              </AvatarFallback>
            </Avatar>
            <div>
              <DialogTitle className="text-lg font-semibold">{getActionTitle()}</DialogTitle>
              <p className="text-sm text-muted-foreground">{customer.name}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Customer Info */}
        <Card className="bg-background border-0">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <Avatar className="w-10 h-10 bg-primary/10">
                <AvatarFallback className="bg-primary/10">
                  <User className="w-5 h-5 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h4 className="font-medium">{customer.name || customer.NamaPelangganTambak}</h4>
                <p className="text-sm text-muted-foreground">{t('customer_selection.id_label')}: {customer.idcust}</p>
                {customer.address && (
                  <p className="text-xs text-muted-foreground mt-1">📍 {customer.address}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Content */}
        <div className="flex-1 min-h-0 max-h-full overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mr-3" />
              <span className="text-muted-foreground">{t('customer_selection.loading_numbers')}</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button
                onClick={fetchPhoneNumbers}
                variant="destructive"
              >
                {t('customer_selection.try_again')}
              </Button>
            </div>
          ) : filteredNumbers.length === 0 ? (
            <div className="p-6 text-center">
              <Avatar className="w-16 h-16 bg-muted mx-auto mb-4">
                <AvatarFallback className="bg-muted">
                  {actionType === 'whatsapp' ? (
                    <MessageCircle className="w-8 h-8 text-muted-foreground" />
                  ) : (
                    <Phone className="w-8 h-8 text-muted-foreground" />
                  )}
                </AvatarFallback>
              </Avatar>
              <h4 className="font-medium text-muted-foreground mb-2">
                {actionType === 'whatsapp' ? t('customer_selection.no_whatsapp') : t('customer_selection.no_phone')}
              </h4>
              <p className="text-muted-foreground text-sm">
                {actionType === 'whatsapp'
                  ? t('customer_selection.no_whatsapp_desc')
                  : t('customer_selection.no_phone_desc')
                }
              </p>
            </div>
          ) : (
            <div className="p-6">
              <h4 className="font-medium text-muted-foreground mb-4">
                {actionType === 'call' ? t('customer_selection.select_number_call') : t('customer_selection.select_number_message')}
              </h4>
              <div className="space-y-3">
                {filteredNumbers.map((phoneNumber, index) => (
                  <Card key={index} className="hover:border-input transition-colors">
                    <CardContent className="p-4">
                      {/* Number Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <Avatar className={`w-8 h-8 ${
                            phoneNumber.type === 'whatsapp' ? 'bg-green-100' : 'bg-primary/10'
                          }`}>
                            <AvatarFallback className={
                              phoneNumber.type === 'whatsapp' ? 'bg-green-100' : 'bg-primary/10'
                            }>
                              {phoneNumber.type === 'whatsapp' ? (
                                <MessageCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Phone className="w-4 h-4 text-primary" />
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium text-muted-foreground">
                              {formatIndonesianNumber(phoneNumber.number)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {getSourceLabel(phoneNumber.source)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="secondary" className={cn(
                            "text-xs font-medium",
                            getConfidenceColor(phoneNumber.confidence)
                          )}>
                            {phoneNumber.confidence} {t('customer_selection.confidence')}
                          </Badge>
                        </div>
                      </div>

                      {/* Context-Specific Action Buttons */}
                      <div className="flex space-x-2">
                        {/* Show Call button for all numbers when actionType is 'call' */}
                        {actionType === 'call' && (
                          <Button
                            onClick={() => handleNumberSelect(phoneNumber, 'call')}
                            className="flex-1 bg-primary hover:bg-primary/90"
                            size="sm"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {t('customer_selection.call')}
                            <ChevronRight className="w-3 h-3 ml-2" />
                          </Button>
                        )}

                        {/* Show WhatsApp button only when actionType is 'whatsapp' and number supports WhatsApp */}
                        {actionType === 'whatsapp' && phoneNumber.type === 'whatsapp' && (
                          <Button
                            onClick={() => handleNumberSelect(phoneNumber, 'whatsapp')}
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            size="sm"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            {t('customer_selection.whatsapp')}
                            <ChevronRight className="w-3 h-3 ml-2" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={onClose}
            variant="secondary"
            className="w-full"
          >
            {t('customer_selection.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};